import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { bulkDeleteProducts, deleteProduct, fetchAllProductsAdmin, fetchProducts } from "../../../../features/productSlice";
import { toast } from "react-toastify";
import API from "../../../../util/API";
import {
  ExternalLink,
  Eye,
  Grid3X3,
  List,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";

const MAX_CATEGORY_PATH_DEPTH = 2; // top category + 2 nested labels
const CORPORATE_SERIF = '"Iowan Old Style", "Times New Roman", serif';
const fieldClass =
  "w-full rounded-md border border-slate-900/20 bg-white px-3 py-2 text-sm font-medium text-slate-900 outline-none transition-all " +
  "placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10 disabled:bg-slate-50 disabled:text-slate-500";
const panelClass = "rounded-md border border-slate-900/15 bg-white";
const primaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-md border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400";
const secondaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 transition-colors hover:border-slate-900/30 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";

const makeCategoryOptionValue = (categoryId, categoryPath = []) => {
  const id = String(categoryId || "").trim();
  if (!id) return "";
  const path = normalizeCategoryPath(categoryPath);
  return `${id}::${encodeURIComponent(JSON.stringify(path))}`;
};

const normalizeCategoryPath = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .slice(0, MAX_CATEGORY_PATH_DEPTH);

const buildCategoryOptions = (categories = []) => {
  const options = [{ value: "", categoryId: "", categoryPath: [], label: "Unassigned" }];

  const pushOption = (categoryId, rootName, path = []) => {
    const normalizedPath = normalizeCategoryPath(path);
    const label = normalizedPath.length
      ? `${rootName} > ${normalizedPath.join(" > ")}`
      : rootName;
    options.push({
      value: makeCategoryOptionValue(categoryId, normalizedPath),
      categoryId,
      categoryPath: normalizedPath,
      label,
    });
  };

  const visitCategory = (root) => {
    const categoryId = String(root?._id || "").trim();
    const rootName = String(root?.name || "").trim();
    if (!categoryId || !rootName) return;
    pushOption(categoryId, rootName, []);

    const walk = (nodes, path = []) => {
      if (!Array.isArray(nodes) || !nodes.length) return;
      if (path.length >= MAX_CATEGORY_PATH_DEPTH) return;

      nodes.forEach((node) => {
        const nodeName = String(node?.name || "").trim();
        if (!nodeName) return;
        const nextPath = [...path, nodeName];
        pushOption(categoryId, rootName, nextPath);
        walk(node?.subcategories, nextPath);
      });
    };

    walk(root?.subcategories, []);
  };

  categories.forEach((category) => visitCategory(category));
  return options;
};

const getShortProductTitle = (value, maxLength = 58) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
};

const normalizeCurrencyCode = (value, fallback = "") => {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : fallback;
};

const ProductList = ({
  products = [],
  pagination,
  page = 1,
  limit = 10,
  onPageChange,
  onLimitChange,
  country = "BD",
  currency = "USD",
  currencyOptions = [],
  search = "",
  onSearchChange,
  countries = [],
  countryNames = {},
  onCountryChange,
  onCurrencyChange,
  priceCountry,
}) => {
  const dispatch = useDispatch();
  const activeCurrency = normalizeCurrencyCode(currency, "USD");

  // Support both controlled (server-side) and uncontrolled (client-side) pagination
  const [internalPage, setInternalPage] = useState(1);
  const [internalLimit, setInternalLimit] = useState(10);

  const isPageControlled = typeof onPageChange === "function";
  const isLimitControlled = typeof onLimitChange === "function";

  const currentPage = isPageControlled ? page : internalPage;
  const itemsPerPage = isLimitControlled ? limit : internalLimit;

  const setPageSafe = (nextPage) => {
    if (typeof onPageChange === "function") return onPageChange(nextPage);
    return setInternalPage(nextPage);
  };

  const setLimitSafe = (nextLimit) => {
    if (typeof onLimitChange === "function") return onLimitChange(nextLimit);
    setInternalLimit(nextLimit);
    setInternalPage(1);
  };

  const [viewMode, setViewMode] = useState("table"); // "grid" | "table"
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryEditorId, setCategoryEditorId] = useState("");
  const [categorySavingId, setCategorySavingId] = useState("");
  const [categoryOverrides, setCategoryOverrides] = useState({});

  // Always store selected IDs as canonical productId (AliExpress ID)
  const [selectedIds, setSelectedIds] = useState([]);

  const categoryOptions = useMemo(() => buildCategoryOptions(categories), [categories]);
  const selectableCurrencies = useMemo(() => {
    const configured = Array.isArray(currencyOptions) ? currencyOptions : [];
    const options = [activeCurrency, ...configured]
      .map((value) => normalizeCurrencyCode(value))
      .filter(Boolean);
    const unique = Array.from(new Set(options));
    return unique.length ? unique : ["USD"];
  }, [activeCurrency, currencyOptions]);

  const makeConfirmWord = () => {
    // 6 chars, easy to type, shown in red
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let out = "";
    for (let i = 0; i < 6; i += 1) out += alphabet[Math.floor(Math.random() * alphabet.length)];
    return out;
  };

  const openDeleteConfirm = ({ mode, ids }) => {
    const cleanIds = (Array.isArray(ids) ? ids : []).filter(Boolean);
    if (!cleanIds.length) return;
    setConfirmDelete({
      mode,
      ids: cleanIds,
      word: makeConfirmWord(),
      input: "",
      busy: false,
    });
  };

  // ---- Helpers ----
  const getProductKey = (product) =>
    String(product?.productId || product?._id || "").trim();

  const getProductImage = (product) => {
    if (Array.isArray(product.images) && product.images.length > 0 && product.images[0]) {
      return product.images[0];
    }
    const skuArr =
      product?.sku?.ae_sku_property_dtos?.ae_sku_property_d_t_o || [];
    const skuImages = skuArr.map((p) => p?.sku_image).filter(Boolean);
    return skuImages[0] || "";
  };

  const getProductTitle = (product) => {
    if (typeof product.name === "string" && product.name.trim()) return product.name;
    if (product?.sku?.sku_attr) return product.sku.sku_attr;
    return "Untitled Product";
  };

  const getProductPrice = (product) => {
    const priceCurrency = product.currency || product?.sku?.converted_currency || activeCurrency;
    const p =
      product.price ??
      product?.sku?.offer_sale_price ??
      product?.sku?.offerPrice ??
      null;
    if (p == null) return "N/A";
    return `${priceCurrency} ${p}`;
  };

  const getCategoryName = (product) => {
    const key = getProductKey(product);
    if (key && categoryOverrides[key]?.label) return categoryOverrides[key].label;

    const name = product?.category?.name || product?.categoryName || product?.category_label;
    const path = normalizeCategoryPath(product?.categoryPath);
    if (!name && !path.length) return "Unassigned";
    if (!name) return path.join(" > ");
    return path.length ? `${name} > ${path.join(" > ")}` : String(name);
  };

  const getCategoryValue = (product) => {
    const key = getProductKey(product);
    const override = key ? categoryOverrides[key] : null;
    const categoryId = override?.categoryId || product?.categoryId || product?.category?._id || "";
    const categoryPath = normalizeCategoryPath(override?.categoryPath || product?.categoryPath || []);
    return makeCategoryOptionValue(categoryId, categoryPath);
  };

  const getSkuCount = (product) => {
    const n = Number(product?.skuCount);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  };

  const getCountriesAvailableCount = (product) => {
    const n = Number(product?.availableCountriesCount);
    if (Number.isFinite(n) && n >= 0) return n;
    const arr = Array.isArray(product?.availableCountries) ? product.availableCountries : null;
    return arr ? arr.length : undefined;
  };

  const getCountriesPreview = (product) => {
    const arr = Array.isArray(product?.availableCountries) ? product.availableCountries : [];
    const clean = arr.map((c) => String(c).toUpperCase()).filter(Boolean);
    return clean.slice(0, 4);
  };

  const resolveCountry = (product) =>
    country === "ALL" ? (product?.country || priceCountry || "US") : country;

  const loadCategories = async () => {
    if (categories.length || categoriesLoading) return;
    try {
      setCategoriesLoading(true);
      const res = await API.get("/category");
      setCategories(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setCategories([]);
      toast.error("Failed to load categories");
    } finally {
      setCategoriesLoading(false);
    }
  };

  const openCategoryEditor = async (product) => {
    const key = getProductKey(product);
    if (!key || categorySavingId === key) return;
    setCategoryEditorId(key);
    await loadCategories();
  };

  const saveProductCategory = async (product, nextValue) => {
    const key = getProductKey(product);
    if (!key) return;

    const option = categoryOptions.find((item) => item.value === nextValue) || categoryOptions[0];
    const currentValue = getCategoryValue(product);
    if (nextValue === currentValue) {
      setCategoryEditorId("");
      return;
    }

    try {
      setCategorySavingId(key);
      const cc = resolveCountry(product);
      await API.put(
        `/product/${encodeURIComponent(key)}?country=${encodeURIComponent(cc)}&currency=${encodeURIComponent(activeCurrency)}`,
        {
          categoryId: option?.categoryId || null,
          categoryPath: normalizeCategoryPath(option?.categoryPath),
        }
      );
      setCategoryOverrides((prev) => ({
        ...prev,
        [key]: {
          categoryId: option?.categoryId || null,
          categoryPath: normalizeCategoryPath(option?.categoryPath),
          label: option?.label || "Unassigned",
        },
      }));
      setCategoryEditorId("");
      toast.success("Category updated");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update category");
    } finally {
      setCategorySavingId("");
    }
  };

  const viewHref = (product) => {
    const pid = product?.productId ? String(product.productId) : '';
    const id = pid || (product?._id ? String(product._id) : '');
    const cc = resolveCountry(product);
    return `/administrator/products/view/${id}?country=${encodeURIComponent(cc)}&currency=${encodeURIComponent(activeCurrency)}`;
  };

  const editHref = (product) => {
    const pid = product?.productId ? String(product.productId) : '';
    const id = pid || (product?._id ? String(product._id) : '');
    const cc = resolveCountry(product);
    return `/administrator/products/editProduct/${id}?country=${encodeURIComponent(cc)}&currency=${encodeURIComponent(activeCurrency)}`;
  };

  const getStatusMeta = (product) => {
    const s = product?.status || "unknown";
    if (s === "active") return { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-100" };
    if (s === "draft") return { label: "Draft", cls: "bg-amber-50 text-amber-700 border-amber-100" };
    if (s === "inactive") return { label: "Inactive", cls: "bg-rose-50 text-rose-700 border-rose-100" };
    return { label: "Unknown", cls: "bg-slate-50 text-slate-700 border-slate-100" };
  };

  const getSourceMeta = (product) => {
    const src = product?.source || "unknown";
    if (src === "aliexpress") return { label: "AliExpress", cls: "bg-orange-50 text-orange-700 border-orange-100" };
    if (src === "local") return { label: "Local", cls: "bg-blue-50 text-blue-700 border-blue-100" };
    if (src === "vendor") return { label: "Vendor", cls: "bg-violet-50 text-violet-700 border-violet-100" };
    return { label: "Unknown", cls: "bg-slate-50 text-slate-700 border-slate-100" };
  };

  // ---- Filtering ----
  const filteredProducts = (() => {
    const q = String(search || '').trim().toLowerCase();

    // When server paginated, backend already applied the filter.
    if (typeof pagination?.total === 'number') return products;

    return products.filter((product) => {
      const hay = `${getProductTitle(product)} ${product.productId || ""} ${product._id || ""} ${getCategoryName(product)}`.toLowerCase();
      return q ? hay.includes(q) : true;
    });
  })();

  // ---- Pagination ----
  const isServerPaginated = typeof pagination?.total === "number";
  const totalCount = isServerPaginated ? pagination.total : filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = isServerPaginated
    ? filteredProducts
    : filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const pageStart = totalCount === 0 || currentProducts.length === 0
    ? 0
    : isServerPaginated
      ? (currentPage - 1) * itemsPerPage + 1
      : Math.min(totalCount, indexOfFirstItem + 1);
  const pageEnd = totalCount === 0 || currentProducts.length === 0
    ? 0
    : isServerPaginated
      ? Math.min(totalCount, (currentPage - 1) * itemsPerPage + currentProducts.length)
      : Math.min(totalCount, indexOfLastItem);

  const getNextPageAfterDeletion = (deletedCount) => {
    const n = Number(deletedCount);
    const removed = Number.isFinite(n) && n > 0 ? n : 0;

    // When server paginated, use the server-reported total.
    if (typeof pagination?.total === "number") {
      const nextTotal = Math.max(0, pagination.total - removed);
      const nextTotalPages = Math.max(1, Math.ceil(nextTotal / itemsPerPage));
      return Math.min(currentPage, nextTotalPages);
    }

    // Fallback for client-side mode.
    const nextTotal = Math.max(0, filteredProducts.length - removed);
    const nextTotalPages = Math.max(1, Math.ceil(nextTotal / itemsPerPage));
    return Math.min(currentPage, nextTotalPages);
  };

  // ---- Refresh ----
  const refetchProducts = async (attempt = 0, pageOverride) => {
    const targetPage = Number.isFinite(Number(pageOverride)) ? Number(pageOverride) : currentPage;
    const action = country === "ALL"
      ? await dispatch(fetchAllProductsAdmin({
          currency: activeCurrency,
          page: targetPage,
          limit: itemsPerPage,
          priceCountry: priceCountry || "US",
          search,
        }))
      : await dispatch(fetchProducts({ country, currency: activeCurrency, page: targetPage, limit: itemsPerPage, append: false, search }));

    if (action?.error && attempt < 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return refetchProducts(attempt + 1);
    }
    return action;
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchProducts();
      toast.success("Products refreshed successfully");
    } catch (error) {
      toast.error("Failed to refresh products");
    } finally {
      setIsRefreshing(false);
    }
  };

  // ---- Delete ----
  const handleDelete = async (id) => {
    try {
      const res = await dispatch(deleteProduct(id));
      if (res?.error) {
        return toast.error(res.error?.message || "Failed to delete product");
      }

      const nextPage = getNextPageAfterDeletion(1);
      if (nextPage !== currentPage) setPageSafe(nextPage);
      await refetchProducts(0, nextPage);

      const msg = res?.payload?.message || "Product deleted successfully.";
      toast.success(msg);
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };


  const pageItems = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages = new Set([1, totalPages]);
    for (let p = currentPage - 1; p <= currentPage + 1; p += 1) {
      if (p > 1 && p < totalPages) pages.add(p);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);

    const out = [];
    for (let i = 0; i < sorted.length; i += 1) {
      out.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push("...");
    }
    return out;
  }, [currentPage, totalPages]);

  // ---- Selection ----
  const isAllPageSelected =
    currentProducts.length > 0 && currentProducts.every((p) => selectedIds.includes(p.productId));

  const toggleSelectAllCurrentPage = () => {
    if (isAllPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !currentProducts.some((p) => p.productId === id)));
    } else {
      setSelectedIds((prev) => {
        const add = currentProducts.map((p) => p.productId);
        return Array.from(new Set([...prev, ...add]));
      });
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const CategoryCell = ({ product, compact = false }) => {
    const key = getProductKey(product);
    const isEditing = categoryEditorId === key;
    const isSaving = categorySavingId === key;
    const categoryName = getCategoryName(product);
    const selectedValue = getCategoryValue(product);
    const hasSelectedOption = categoryOptions.some((option) => option.value === selectedValue);

    if (isEditing) {
      return (
        <select
          autoFocus
          value={selectedValue}
          disabled={categoriesLoading || isSaving}
          onBlur={() => {
            if (!isSaving) setCategoryEditorId("");
          }}
          onChange={(event) => saveProductCategory(product, event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setCategoryEditorId("");
          }}
          className={[
            "w-full rounded-md border border-slate-900/20 bg-white px-2 py-1 text-xs font-semibold text-slate-800 outline-none",
            "focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10",
          ].join(" ")}
        >
          {categoriesLoading ? <option value={selectedValue}>Loading categories...</option> : null}
          {!categoriesLoading && selectedValue && !hasSelectedOption ? (
            <option value={selectedValue}>{categoryName}</option>
          ) : null}
          {!categoriesLoading &&
            categoryOptions.map((option) => (
              <option key={option.value || "unassigned"} value={option.value}>
                {option.label}
              </option>
            ))}
        </select>
      );
    }

    return (
      <button
        type="button"
        disabled={isSaving}
        onDoubleClick={() => openCategoryEditor(product)}
        title="Double-click to change category"
        className={[
          "max-w-full rounded-md border border-slate-200 bg-white text-left font-semibold text-slate-700 hover:border-slate-900/30 hover:bg-slate-50",
          compact ? "px-2 py-1 text-[11px]" : "px-2.5 py-1.5 text-xs",
          isSaving ? "opacity-60" : "",
        ].join(" ")}
      >
        <span className="block truncate">{isSaving ? "Saving..." : categoryName}</span>
      </button>
    );
  };

  // ---- UI: Card ----
  const ProductCard = ({ product }) => {
    const imageUrl = getProductImage(product);
    const title = getProductTitle(product);
    const price = getProductPrice(product);
    const status = getStatusMeta(product);
    const src = getSourceMeta(product);
    const skuCount = getSkuCount(product);
    const countriesCount = getCountriesAvailableCount(product);
    const countriesPreview = getCountriesPreview(product);

    const isSelected = selectedIds.includes(product.productId);

    return (
      <div
        className={[
          "overflow-hidden rounded-md border bg-white transition-all",
          isSelected ? "border-slate-900 ring-4 ring-slate-900/10" : "border-slate-900/15 hover:border-slate-900/30",
        ].join(" ")}
      >
        {/* Top row */}
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelectOne(product.productId)}
              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20"
            />
            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">Select</span>
          </label>

          <span className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${status.cls}`}>
            {status.label}
          </span>
        </div>

        {/* Image */}
        <div className="p-4">
          <div className="aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="text-sm font-medium text-slate-400">No Image</div>
            )}
          </div>

          <div className="mt-4 space-y-2">
            <h3 className="text-sm font-semibold text-slate-900 line-clamp-2" title={title}>
              {title}
            </h3>

            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-slate-900">{price}</span>
              <span className="text-xs text-slate-500">ID: {product.productId || "—"}</span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700">
                {resolveCountry(product)} / {activeCurrency}
              </span>
              {typeof countriesCount === 'number' ? (
                <span className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700">
                  Countries: {countriesCount}
                </span>
              ) : null}
              {typeof skuCount === 'number' ? (
                <span className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700">
                  SKUs: {skuCount}
                </span>
              ) : null}
              {countriesPreview.length ? (
                <span className="text-[11px] text-slate-500">
                  {countriesPreview.join(', ')}{countriesCount > countriesPreview.length ? ', ...' : ''}
                </span>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Source:</span>
              <span className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${src.cls}`}>{src.label}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Category:</span>
              <div className="min-w-0 flex-1">
                <CategoryCell product={product} compact />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <Link
              to={viewHref(product)}
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-black"
            >
              <Eye size={13} />
              View
            </Link>
            <Link
              to={editHref(product)}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-900/30 hover:bg-slate-50"
            >
              <Pencil size={13} />
              Edit
            </Link>
          </div>

          <div className="flex items-center gap-2">
            {product.productId && (
              <a
                href={`https://www.aliexpress.com/item/${product.productId}.html`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900"
                title="View on AliExpress"
                aria-label="View on AliExpress"
              >
                <ExternalLink size={14} />
              </a>
            )}

            <button
              onClick={() => openDeleteConfirm({ mode: 'single', ids: [product.productId] })}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-rose-600 transition-colors hover:border-rose-200 hover:bg-rose-50"
              title="Delete"
              aria-label="Delete product"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ---- UI: Table ----
  const ProductTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[980px] table-fixed text-sm">
        <thead className="border-b border-slate-100 bg-slate-50">
          <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            <th className="w-10 px-3 py-2.5">
              <input
                type="checkbox"
                checked={isAllPageSelected}
                onChange={toggleSelectAllCurrentPage}
                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20"
              />
            </th>
            <th className="w-[42%] px-3 py-2.5">Product</th>
            <th className="w-[13%] px-3 py-2.5">Price</th>
            <th className="w-[24%] px-3 py-2.5">Category</th>
            <th className="w-[11%] px-3 py-2.5">Reach</th>
            <th className="sticky right-0 z-20 w-[168px] bg-slate-50 px-3 py-2.5 text-right shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.25)]">Actions</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100">
          {currentProducts.map((product) => {
            const key = getProductKey(product);
            const imageUrl = getProductImage(product);
            const title = getProductTitle(product);
            const shortTitle = getShortProductTitle(title);
            const price = getProductPrice(product);
            const status = getStatusMeta(product);
            const src = getSourceMeta(product);
            const isSelected = selectedIds.includes(product.productId);
            const skuCount = getSkuCount(product);
            const countriesCount = getCountriesAvailableCount(product);
            const countriesPreview = getCountriesPreview(product);

            return (
              <tr key={key || product._id} className="group transition-colors hover:bg-slate-50/70">
                <td className="px-3 py-2.5 align-middle">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelectOne(product.productId)}
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20"
                  />
                </td>

                <td className="px-3 py-2.5 align-middle">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={title}
                          className="h-full w-full object-cover"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <span className="text-xs font-semibold text-slate-300">No Image</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="max-w-[300px] truncate font-semibold text-slate-900" title={title}>{shortTitle}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-[11px] text-slate-500">ID: {product.productId || "-"}</span>
                        <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${status.cls}`}>
                          {status.label}
                        </span>
                        <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${src.cls}`}>
                          {src.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>

                <td className="px-3 py-2.5 align-middle">
                  <div className="font-semibold text-slate-900">{price}</div>
                  <div className="mt-1 text-[11px] font-semibold text-slate-500">
                    {resolveCountry(product)} / {activeCurrency}
                  </div>
                </td>

                <td className="px-3 py-2.5 align-middle">
                  <CategoryCell product={product} compact />
                </td>

                <td className="px-3 py-2.5 align-middle">
                  <div className="font-semibold text-slate-900">
                    {typeof countriesCount === "number" ? `${countriesCount} countries` : "-"}
                  </div>
                  <div className="mt-1 text-[11px] font-semibold text-slate-500">
                    {typeof skuCount === "number" ? `${skuCount} SKUs` : "-"}
                  </div>
                  {countriesPreview.length ? (
                    <div className="mt-1 truncate font-mono text-[10px] text-slate-400" title={countriesPreview.join(", ")}>
                      {countriesPreview.join(", ")}{countriesCount > countriesPreview.length ? ", ..." : ""}
                    </div>
                  ) : null}
                </td>

                <td className="sticky right-0 z-10 bg-white px-3 py-2.5 align-middle shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.25)] group-hover:bg-slate-50">
                  <div className="flex items-center justify-end gap-1.5">
                    <Link
                      to={viewHref(product)}
                      className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-black"
                    >
                      <Eye size={13} />
                      View
                    </Link>

                    <Link
                      to={editHref(product)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-900/30 hover:bg-slate-50"
                    >
                      <Pencil size={13} />
                      Edit
                    </Link>

                    <button
                      onClick={() => openDeleteConfirm({ mode: "single", ids: [product.productId] })}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-600 transition-colors hover:border-rose-200 hover:bg-rose-50"
                    >
                      <Trash2 size={13} />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
  // ---- Render ----
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2
            className="text-[1.9rem] leading-[1.03] text-slate-900"
            style={{ fontFamily: CORPORATE_SERIF }}
          >
            Product Catalog
          </h2>
          <p className="mt-2 text-sm text-slate-600">Manage and organize your product inventory.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700">
              Enabled countries: {Array.isArray(countries) ? countries.length : 0}
            </span>
            <span className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700">
              Current: {countryNames?.[country] ? `${countryNames[country]} (${country})` : country}
            </span>
            <span className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700">
              Currency: {activeCurrency}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/administrator/products/addProduct/aliexpress"
            className={primaryButtonClass}
          >
            <Plus size={16} />
            Add Product
          </Link>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={secondaryButtonClass}
          >
            <RefreshCw size={16} className={isRefreshing ? "animate-spin" : ""} />
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={`${panelClass} p-4 md:p-5`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">Country</label>
            <select
              className={fieldClass}
              value={country}
              onChange={(e) => {
                const next = e.target.value;
                if (typeof onCountryChange === 'function') onCountryChange(next);
              }}
            >
              {(Array.isArray(countries) ? countries : []).map((cc) => (
                <option key={cc} value={cc}>
                  {countryNames?.[cc] ? `${countryNames[cc]} (${cc})` : cc}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">Currency</label>
            <select
              className={fieldClass}
              value={activeCurrency}
              onChange={(e) => {
                const next = normalizeCurrencyCode(e.target.value, activeCurrency);
                if (typeof onCurrencyChange === "function") onCurrencyChange(next);
              }}
              disabled={typeof onCurrencyChange !== "function" || selectableCurrencies.length <= 1}
            >
              {selectableCurrencies.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => {
                  const next = e.target.value;
                  if (typeof onSearchChange === 'function') onSearchChange(next);
                }}
                placeholder="Search by name or ID"
                className={`${fieldClass} pl-10`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* View toggle + bulk actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center rounded-md border border-slate-200 bg-white p-1">
            <button
              onClick={() => setViewMode("grid")}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                viewMode === "grid" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Grid3X3 size={15} />
              Grid
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                viewMode === "table" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <List size={15} />
              Table
            </button>
          </div>

          <span className="text-sm text-slate-600">
            {currentProducts.length} of {totalCount}
          </span>

          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Rows</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setLimitSafe(Number(e.target.value))}
              className="rounded-md border border-slate-200 px-3 py-2 text-sm font-medium focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
            </select>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">{selectedIds.length} selected</span>
            <button
              onClick={() => openDeleteConfirm({ mode: 'bulk', ids: selectedIds })}
              className="inline-flex items-center gap-2 rounded-md border border-rose-600 bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-700"
            >
              <Trash2 size={15} />
              Delete Selected
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {filteredProducts.length === 0 ? (
        <div className={`${panelClass} py-14 text-center`}>
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-500">
            <Search size={18} />
          </div>
          <div className="mt-3 text-sm font-semibold text-slate-900">No products found</div>
          <div className="mt-1 text-sm text-slate-500">Try changing country, adjusting search, or refresh.</div>
          <div className="mt-5 flex items-center justify-center gap-2">
            <Link
              to="/administrator/products/addProduct/aliexpress"
              className={primaryButtonClass}
            >
              <Plus size={16} />
              Create Product
            </Link>
          </div>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {currentProducts.map((p) => (
            <ProductCard key={p._id} product={p} />
          ))}
        </div>
      ) : (
        <div className={`${panelClass} overflow-hidden`}>
          <ProductTable />
        </div>
      )}

      {/* Pagination */}
      {totalCount > 0 && (
        <div className={`${panelClass} p-4 md:p-5`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="text-sm text-slate-600">
                Showing{" "}
                <span className="font-semibold text-slate-900">
                  {pageStart}
                </span>
                -
                <span className="font-semibold text-slate-900">
                  {pageEnd}
                </span>
                {" "}of <span className="font-semibold text-slate-900">{totalCount}</span>
              </div>
            </div>

            {/* Page controls */}
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={() => setPageSafe(1)}
                disabled={currentPage === 1}
                className={secondaryButtonClass}
              >
                First
              </button>
              <button
                onClick={() => setPageSafe(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={secondaryButtonClass}
              >
                Prev
              </button>

              <div className="flex items-center gap-1">
                {pageItems.map((it, idx) =>
                  it === "..." ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-slate-400 select-none">
                      ...
                    </span>
                  ) : (
                    <button
                      key={it}
                      onClick={() => setPageSafe(it)}
                      className={
                        "h-9 min-w-9 rounded-md border px-3 text-sm font-semibold transition-colors " +
                        (it === currentPage
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50")
                      }
                    >
                      {it}
                    </button>
                  )
                )}
              </div>

              <button
                onClick={() => setPageSafe(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={secondaryButtonClass}
              >
                Next
              </button>
              <button
                onClick={() => setPageSafe(totalPages)}
                disabled={currentPage === totalPages}
                className={secondaryButtonClass}
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-md overflow-hidden rounded-md border border-slate-200 bg-white">
            <div className="p-5 border-b border-slate-100">
              <p className="text-base font-semibold text-slate-900">
                {confirmDelete.mode === 'bulk' ? `Delete ${confirmDelete.ids.length} products?` : 'Delete product?'}
              </p>
              <p className="text-sm text-slate-600 mt-1">This action cannot be undone.</p>
            </div>

            <div className="p-5 space-y-3">
              <div className="text-sm text-slate-700">
                Type <span className="font-semibold text-rose-600">{confirmDelete.word}</span> to confirm.
              </div>
              <input
                value={confirmDelete.input}
                onChange={(e) => setConfirmDelete((prev) => ({ ...prev, input: e.target.value }))}
                placeholder="Type the red code"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
                autoFocus
              />
            </div>

            <div className="p-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className={secondaryButtonClass}
              >
                <X size={15} />
                Cancel
              </button>
              <button
                disabled={
                  confirmDelete.busy ||
                  String(confirmDelete.input || '').trim() !== String(confirmDelete.word)
                }
                onClick={async () => {
                  setConfirmDelete((prev) => ({ ...prev, busy: true }));
                  try {
                    if (confirmDelete.mode === 'bulk') {
                      const res = await dispatch(bulkDeleteProducts(confirmDelete.ids));
                      if (res?.error) {
                        throw new Error(res.error?.message || "Failed to delete selected products");
                      }
                      const nextPage = getNextPageAfterDeletion(confirmDelete.ids.length);
                      if (nextPage !== currentPage) setPageSafe(nextPage);
                      await refetchProducts(0, nextPage);
                      toast.success(`${confirmDelete.ids.length} products deleted successfully`);
                      setSelectedIds([]);
                    } else {
                      await handleDelete(confirmDelete.ids[0]);
                    }
                    setConfirmDelete(null);
                  } catch (e) {
                    toast.error("Failed to delete");
                    setConfirmDelete(null);
                  }
                }}
                className="inline-flex items-center gap-2 rounded-md border border-rose-600 bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Trash2 size={15} />
                {confirmDelete.busy ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;
